import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { Panel } from '@/components/erp/primitives'
import { UnitMap } from '@/shared/map/UnitMap'
import { hasMapbox } from '@/shared/map/mapbox'
import { EmptyState, ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { FilterBar, MultiSelect } from '@/shared/ui/filters'
import { mapMarkers, type MapMarker } from './api'
import { useVocabulary } from './useIncident'
import { IncidentDrawer } from './IncidentDrawer'
import { SEVERITY_ORDER, SeverityChip, StatusChip, label, options, severityMarker } from './vocab'

/**
 * Incidents on a map, by severity.
 *
 * ## Severity is drawn on the ORDINAL ramp
 *
 * LOW < MEDIUM < HIGH < CRITICAL is an ordered scale. Painting it with a categorical
 * palette — which is what PollSphere does — asks the reader to memorise which hue means
 * worse, and gets it wrong for anyone with a colour vision deficiency. A monotone ramp
 * says the order in the colour itself, and every marker is also listed with its severity
 * in words, so the colour never carries the meaning alone.
 *
 * ## `/incidents/map` and not `/incidents`
 *
 * The marker payload is lat/lng/severity/status/ref only. Loading the full register to
 * draw dots would pull description, impact counters and legal references over the wire for
 * every pin.
 *
 * ## Mapbox may be absent
 *
 * With no token the page still works: the list beside the map is the real content, and the
 * map is the garnish. It says why it is missing rather than rendering an empty rectangle.
 */
export function MapPage() {
  const [params, setParams] = useSearchParams()
  const { vocab } = useVocabulary()
  const [severity, setSeverity] = useState<string[]>(params.getAll('severity'))
  const [status, setStatus] = useState<string[]>(params.getAll('status'))
  const [rows, setRows] = useState<MapMarker[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const openId = params.get('open')

  const load = useCallback(async () => {
    setError(null)
    try {
      setRows(await mapMarkers({ severity: severity[0] ?? null, status: status[0] ?? null }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [severity, status])

  // Both filters listed. With `[]` the map would draw the unfiltered set forever.
  useEffect(() => { void load() }, [load])

  const placed = useMemo(
    () => (rows ?? []).filter((r) => r.latitude != null && r.longitude != null),
    [rows])
  const unplaced = (rows?.length ?? 0) - placed.length

  const narrowed = placed.filter((r) =>
    (severity.length === 0 || severity.includes(r.severityCode))
    && (status.length === 0 || status.includes(r.statusCode)))

  return (
    <div className="space-y-3">
      <FilterBar activeCount={(severity.length > 0 ? 1 : 0) + (status.length > 0 ? 1 : 0)}>
        <MultiSelect label="Severity" options={options(vocab?.severities)}
          selected={severity} onChange={setSeverity} />
        <MultiSelect label="Status" options={options(vocab?.statuses)}
          selected={status} onChange={setStatus} />
      </FilterBar>

      <ErrorStrip message={error} className="rounded-md border" />

      <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)' }}>
        <Panel title="Where incidents are"
          description={hasMapbox()
            ? 'Colour is the severity ramp — darker is worse. Click a marker, or a row, to open the incident.'
            : undefined}>
          {hasMapbox() ? (
            <UnitMap
              mode="point"
              label="Incidents"
              /*
                 Every incident is a marker, and every marker opens the SAME drawer the
                 register opens. A map that plots dots you cannot click is a picture of the
                 data rather than a way into it — the desk sees a cluster and then has to go
                 hunting for it by hand in the list.
              */
              markers={narrowed.map((m) => ({
                id: m.id,
                lat: Number(m.latitude),
                lon: Number(m.longitude),
                ...severityMarker(m.severityCode),
                title: `${m.ref} — ${label(m.severityCode)} · ${label(m.statusCode)}`,
              }))}
              onMarkerClick={(id) =>
                setParams((p) => { p.set('open', id); return p }, { replace: true })}
              className="h-[28rem] rounded-md overflow-hidden border border-border"
            />
          ) : (
            <p className="text-erp-sm text-muted-foreground border-l-2 border-border pl-3 py-1">
              The map is not available — this deployment has no Mapbox token configured. The
              list beside it carries every incident that has a coordinate, with its severity
              and status.
            </p>
          )}
          <SeverityLegend />
        </Panel>

        <Panel title={`${narrowed.length} placed`}
          description={unplaced > 0 ? `${unplaced} more have no coordinate` : undefined}>
          {rows === null ? <LoadingBlock label="Loading markers…" /> : narrowed.length === 0 ? (
            <EmptyState
              title="Nothing to plot"
              hint={unplaced > 0
                ? `${unplaced} incidents match, but none of them recorded a coordinate. That is a real answer — the register shows them.`
                : 'No incident in your scope matches these filters.'}
            />
          ) : (
            <ul className="space-y-1 max-h-[26rem] overflow-y-auto">
              {narrowed.map((m) => (
                <li key={m.id}>
                  <button type="button"
                    onClick={() => setParams((p) => { p.set('open', m.id); return p }, { replace: true })}
                    className="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted/50 flex items-start gap-2">
                    <span aria-hidden className="rounded-full shrink-0 mt-1 border border-card"
                      style={{
                        background: severityMarker(m.severityCode).color,
                        width: severityMarker(m.severityCode).size / 2,
                        height: severityMarker(m.severityCode).size / 2,
                      }} />
                    <span className="min-w-0 flex-1">
                      <span className="font-mono text-erp-xs text-muted-foreground block">{m.ref}</span>
                      <span className="text-erp-sm truncate block">
                        {m.locationLabel ?? `${m.latitude}, ${m.longitude}`}
                      </span>
                      <span className="flex flex-wrap gap-1 mt-0.5">
                        <SeverityChip code={m.severityCode} />
                        <StatusChip code={m.statusCode} />
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <IncidentDrawer
        id={openId}
        onClose={() => setParams((p) => { p.delete('open'); return p }, { replace: true })}
        onChanged={load}
      />
    </div>
  )
}

/**
 * The ramp, in order, at the marker's real size and colour.
 *
 * Drawn at the same size the map uses rather than as four equal swatches, because size IS
 * the encoding here as much as colour is — a legend that flattens it would explain half
 * the chart. Colour never travels alone: every entry carries its word.
 */
function SeverityLegend() {
  return (
    <ul className="mt-3 flex flex-wrap items-center gap-4 text-erp-xs">
      {SEVERITY_ORDER.map((code) => {
        const marker = severityMarker(code)
        return (
          <li key={code} className="inline-flex items-center gap-1.5">
            <span aria-hidden className="rounded-full border-2 border-card shrink-0"
              style={{ background: marker.color, width: marker.size, height: marker.size }} />
            {label(code)}
          </li>
        )
      })}
      <li className="text-muted-foreground/70">Bigger and darker is worse.</li>
    </ul>
  )
}
